import { BetGeniusScoreboard } from '../betGeniusScoreboard/BetGeniusScoreboard';
import React from 'react';
import { ScoreBoardProps } from './index';
import { ScoreBoardProvider } from 'features/sport';
import { SkeletonArea } from 'components/skeletonArea';
import { SnaiScoreboard } from '../snaiScoreboard/SnaiScoreboard';
import { SportResultsAndInfoContent } from 'features/sport/components/sportResultsAndInfoContent';
import { StatisticType } from 'features/sport/utils/betRadar/statisticTypeEnum';
import { selectAvvenimentoHeaderInfo, selectIsInfoOpen } from 'features/sport/selectors';
import { useMemo } from 'react';
import { useTypedSelector } from 'lib/centralStore';

export const ScoreBoard = ({ avvenimentoKey }: ScoreBoardProps): JSX.Element => {
  const { scoreboardHasError } = useTypedSelector(({ sport }) => sport);
  const { hasError, provider } = scoreboardHasError;
  const isInfoOpen = useTypedSelector(selectIsInfoOpen);

  const { isLive, coodsId, providerId, idProgramma, idDisciplina, idAvvenimento, betRadarMatchId, betGeniusMatchId } =
    useTypedSelector(selectAvvenimentoHeaderInfo);

  return useMemo(() => {
    if (providerId) {
      if (isLive) {
        if (!hasError) {
          switch (providerId) {
            case ScoreBoardProvider.Snai: {
              return (
                <SnaiScoreboard
                  key={`SnaiScoreboard-${avvenimentoKey}`}
                  isOpen={isInfoOpen}
                  disciplina={idDisciplina}
                  codProgramme={idProgramma}
                  idAvvenimento={idAvvenimento}
                  betRadarMatchId={betRadarMatchId}
                  betGeniusMatchId={betGeniusMatchId}
                />
              );
            }
            case ScoreBoardProvider.BetGenius: {
              return (
                <BetGeniusScoreboard
                  key={`BetGeniusScoreboard-${avvenimentoKey}-${betGeniusMatchId}`}
                  isOpen={isInfoOpen}
                  disciplina={idDisciplina!}
                  providerID={betGeniusMatchId?.toString()!}
                />
              );
            }
            case ScoreBoardProvider.BetRadar:
            default: {
              const id = (Number(providerId) || betRadarMatchId) ?? 0;
              if (!!coodsId) {
                return (
                  <SportResultsAndInfoContent
                    id={id}
                    key={`ResultsAndInfoContent-${avvenimentoKey}-${id}`}
                    type={StatisticType.Cars}
                    isOpen={isInfoOpen}
                    stageId={Number(coodsId)!}
                  />
                );
              }
              return (
                <SportResultsAndInfoContent
                  id={betRadarMatchId}
                  key={`ResultsAndInfoContent-${avvenimentoKey}-${id}`}
                  type={isLive ? StatisticType.AvvenimentoLive : StatisticType.Avvenimento}
                  isOpen={isInfoOpen}
                  providerID={providerId}
                />
              );
            }
          }
        } else if (provider === ScoreBoardProvider.BetGenius) {
          return (
            <BetGeniusScoreboard
              key={`BetGeniusScoreboard-${avvenimentoKey}-${betGeniusMatchId}`}
              isOpen={isInfoOpen}
              disciplina={idDisciplina!}
              providerID={betGeniusMatchId?.toString()!}
            />
          );
        }

        return (
          <SnaiScoreboard
            key={`SnaiScoreboard-${avvenimentoKey}`}
            isOpen={isInfoOpen}
            disciplina={idDisciplina}
            codProgramme={idProgramma}
            idAvvenimento={idAvvenimento}
            betRadarMatchId={betRadarMatchId}
            betGeniusMatchId={betGeniusMatchId}
          />
        );
      } else {
        return (
          <SportResultsAndInfoContent
            key={`ResultsAndInfoContent-${avvenimentoKey}`}
            type={StatisticType.Avvenimento}
            isOpen={isInfoOpen}
            providerID={providerId}
          />
        );
      }
    }
    if (isInfoOpen) {
      return <SkeletonArea key={`Skeleton-${avvenimentoKey}`} height="15" />;
    }
    return <React.Fragment key={`Empty-${avvenimentoKey}`} />;
  }, [
    isLive,
    coodsId,
    hasError,
    providerId,
    isInfoOpen,
    idProgramma,
    idDisciplina,
    idAvvenimento,
    avvenimentoKey,
    betRadarMatchId,
    betGeniusMatchId,
    provider,
  ]);
};
